import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {LayersSet} from "../../../game/reels/LayersSet";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {gsap} from "gsap";
import {BrickSnake2000Symbol} from "../../../game/reels/symbols/BrickSnake2000Symbol";
import {CustomEase} from "gsap/dist/CustomEase";
import {BrickSnake2000} from "../../../game/BrickSnake2000";

/**
 * Created by jonas on 2022-06-10.
 */

export enum SimpleReelModelState {
    IDLE = "IDLE",
    START = "START",
    SPIN = "SPIN",
    STOP = "STOP",
    STOPPED = "STOPPED",
    BOUNCE = "BOUNCE",
    WIND_UP = "WIND_UP",
    NUDGE = "NUDGE",
    STICKY = "STICKY",
    FILL_UP = "FILL_UP"
}

export interface SimpleReelModelEventData {
    reel:number;
    state:SimpleReelModelState;
}

export interface SimpleReelModelSymbolPosition {
    reelID:number;
    position:number
}

export interface SimpleReelPaddingData {
    visualPaddingBefore:number;
    paddingBefore:number;
    visibleSymbols:number;
    paddingAfter:number;
    visualPaddingAfter:number;
}

export interface SimpleReelModelDelegate {
    //createSymbol(symbolName:string, state:SimpleReelModelState, position:SimpleReelModelSymbolPosition):TheCageSymbol;
    createSymbol(symbolName:string, state:SimpleReelModelState, reelID:number, position:number):BrickSnake2000Symbol;
    removeSymbol(symbolData:BrickSnake2000Symbol):void;
}

export interface ReelView {
    layers: LayersSet,
}

export class SimpleReelModel {

    public readonly events:APIEventSystem | undefined;
    public readonly id:number;
    protected _delegate:SimpleReelModelDelegate;

    constructor(id:number, delegate:SimpleReelModelDelegate, triggerEvents:boolean = false) {
        this.id = id;
        this._delegate = delegate;
        if (triggerEvents) {
            this.events = SlotGame.apiPlugin.eventSystemFactory.create();
            this.events.on("ReelStateChange", (data:SimpleReelModelEventData) => this.onReelStateChange(data)); //To prevent event system from saving once callbacks if no on callbacks is set.
        }
    }

    private onReelStateChange(data:SimpleReelModelEventData):void {
        this.events?.trigger(data.state, data);
    }

    protected createNewSymbol(name:string, position:number, state:SimpleReelModelState):BrickSnake2000Symbol {
        return this._delegate.createSymbol(name, state, this.id, position);
    }

    protected clearSymbols(symbols:BrickSnake2000Symbol[]):void {
        let symbol;
        while (symbol = symbols.pop()) {
            this._delegate.removeSymbol(symbol);
        }
    }

    private _triggerTime:number;
    public illegalVisualPaddingSymbols:string[] = [];
    public active:boolean = true;
    public collectable:boolean = true;

    get reelSet():string[] {
        if (!this._reelSet) {
            throw new Error(`No reelset set for reel: ${this.id}`);
        }
        return this._reelSet;
    }

    get stopTriggerTl():gsap.core.Timeline | undefined {
        return this._stopTriggerTl;
    }

    private _stopTriggerTl:gsap.core.Timeline | undefined;

    get stopTimeline():gsap.core.Timeline | undefined {
        return this._stopTimeline;
    }

    get speed():number {
        return this._speed;
    }
    
    set speed(value:number) {
        this._speed = value;
        this.animationTimeScale = value * this.stepDuration;
        this.masterSpinTimeline?.timeScale(this.animationTimeScale);
    }

    get state():SimpleReelModelState {
        return this._state;
    }

    set state(value:SimpleReelModelState) {
        this._state = value;
        if (this.events) {
            this.events.trigger("ReelStateChange", <SimpleReelModelEventData>{reel:this.id, state:this._state});
        }
    }

    get symbols():BrickSnake2000Symbol[] {
        return this._symbols;
    }

    get stopSymbols():BrickSnake2000Symbol[] {
        return this._stopSymbols;
    }


    //Fake reel
    private _reelSet:string[] | undefined;
    private _reelSetIndex:number = 0;

    private _stopIndex:number;
    private _stopSymbols:BrickSnake2000Symbol[] = [];
    private _symbols:BrickSnake2000Symbol[] = [];

    public paddingConfig:SimpleReelPaddingData = {
        visualPaddingBefore:0,
        paddingBefore:1,
        visibleSymbols:5,
        paddingAfter:1,
        visualPaddingAfter:0,
    }


    public animatedStepData = {
        x:0,
        y:0
    }

    public stepSize:number = 180;
    private _spinning:boolean = false;
    private _currentSpinStep:gsap.core.Tween | undefined;

    private _anticipationDurFactor:number = 2;

    private animationTimeScale = 1;
    public stepDuration = 0.1;

    private _stopTimeline:gsap.core.Timeline | undefined;

    private _stopping:boolean = false;
    private triggerStop:boolean = false;


    private _startedReverse:boolean;


    private _speed:number = this.animationTimeScale / this.stepDuration;
    private masterSpinTimeline:gsap.core.Timeline | undefined;


    private _state:SimpleReelModelState = SimpleReelModelState.IDLE;

    public setReelSet(reelSet:string[], index?:number) {
        this._reelSet = reelSet;
        index = index != undefined ? index : Math.floor(Math.random() * this._reelSet.length);
        this.setReelIndex(index);
    }

    private getNextSymbolName(nextSymbolPosition:number):string {
        let nextIndex = this._reelSetIndex + nextSymbolPosition;
        if (nextIndex < 0) {
            nextIndex = this.reelSet.length + nextIndex;
        }
        if (nextIndex >= this.reelSet.length) {
            nextIndex = nextIndex - this.reelSet.length;
        }

        // Do not create xNudge as overflow if we are stopping the reels
        if(this.reelSet[nextIndex] == "XN" && this.state == SimpleReelModelState.STOP) {
            nextIndex = nextIndex+1 % this.reelSet.length;
        }
        return this.reelSet[nextIndex];
    }

    private getNextSymbol(position:number, state:SimpleReelModelState, isNudging:boolean = false):BrickSnake2000Symbol {
        //Stopping
        if (this._stopping && this._stopSymbols.length > 0) {
            const symbol = this._stopSymbols[this._stopIndex];
            symbol.reelPosition = position;
            return symbol;
        }

        // Ugly fix to make a temporary padding symbol behind xNudges instead of just hiding the actual symbol.
        // Just hiding the symbol may cause a crash when the symbol behind is a xWays symbol
        return this.createNewSymbol(isNudging ? "P" : this.getNextSymbolName(position), position, state);
    }

    private getRandomSymbolVisualPaddingSymbol():string {
        const randomIndex = Math.floor(Math.random() * this.reelSet.length);
        let symbol = this.reelSet[randomIndex];
        if (this.illegalVisualPaddingSymbols.indexOf(symbol) >= 0) {
            symbol = this.getRandomSymbolVisualPaddingSymbol();
        }
        return symbol;
    }

    private getRandomSymbolInReelSet():string {
        const randomIndex = Math.floor(Math.random() * this.reelSet.length);
        return this.reelSet[randomIndex];
    }

    private getRandomSymbols(count:number):string[] {
        const result:string[] = [];
        for (let i:number = 0; i < count; i++) {
            result.push(this.getRandomSymbolVisualPaddingSymbol());
        }
        return result;
    }


    //Public functions

    public init(symbols:string[]) {
        this.clearSymbols(this._symbols);
        this.clearSymbols(this._stopSymbols);

        const totalCount = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;
        const totalInitCount = this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter;

        if (symbols.length != totalInitCount) {
            Logger.warn(`ReelModel.init:  Symbol count mismatch in reel ${this.id}`);
        }

        symbols = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(symbols);
        symbols = symbols.concat(this.getRandomSymbols(this.paddingConfig.visualPaddingAfter));

        for (let i:number = 0; i < symbols.length; i++) {
            const symbol = this.createNewSymbol(symbols[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.IDLE);
            this._symbols.push(symbol);
        }

        this.updatePosition();
    }

    public halt() {
        if (this._currentSpinStep) {
            this.masterSpinTimeline?.kill();
            this._currentSpinStep?.progress(1);
        }
    }

    public resume() {
        this.startSpin();
    }

    public stop(stopSymbolNames:string[], reverse:boolean = false, anticipation:boolean = false):gsap.core.Timeline {
        reverse = this._startedReverse;
        if (this.state != SimpleReelModelState.START && this.state != SimpleReelModelState.SPIN){
            return gsap.timeline();
        }

        this.clearSymbols(this._stopSymbols);
        this._stopSymbols = [];
        const numberOfNormalSteps = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;

        while (stopSymbolNames.length < numberOfNormalSteps) {
            stopSymbolNames.unshift(this.getRandomSymbolVisualPaddingSymbol());
            stopSymbolNames.push(this.getRandomSymbolVisualPaddingSymbol());
        }

        for (let i:number = stopSymbolNames.length - 1; i >= 0; i--) {
            const symbol = this.createNewSymbol(stopSymbolNames[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.STOP);
            //symbol.positionable.position.x = this.stepSize.x * -(this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols);
            symbol.position.y = this.stepSize * -(this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols);
            this._stopSymbols.unshift(symbol);
        }

        this._stopTriggerTl = gsap.timeline({
            paused:true,
            onStart:() => {
                this.state = SimpleReelModelState.STOP;
                this._spinning = false;
                this.triggerStop = true;
                this._triggerTime = Date.now();
            }
        });

        this._stopTimeline = gsap.timeline({
            paused:true,
            onStart:() => {
                //  console.log(Date.now() - this._triggerTime);
                this._stopIndex = stopSymbolNames.length - 1;
                this._stopping = true;

                if(anticipation) {
                    // hack job to make it possible to have multiple symbols (snake parts is the only use case) on the reels
                    this._stopSymbols.filter(sym => sym.symbolName != "C").forEach(sym => {
                        if (sym instanceof BrickSnake2000Symbol)
                            sym.crossFadeFromSpin(0.17);
                    });
                    this._symbols.forEach(sym => {
                        if (sym instanceof BrickSnake2000Symbol)
                            sym.crossFadeFromSpin(0.17);
                    });
                } else {
                    this._stopSymbols.forEach(sym => {
                        if (sym instanceof BrickSnake2000Symbol)
                            sym.crossFadeFromSpin(0.15);
                    });
                }
            },
            onComplete:() => {
                this.state = SimpleReelModelState.STOPPED;
                this._stopTimeline = undefined;
                this._stopSymbols = [];
                this._stopping = false;
                this.state = SimpleReelModelState.IDLE;
            }
        });


        if(anticipation) {
            this._stopTimeline.add(this.stepsAnticipation(numberOfNormalSteps, 0.5));
            // If wincap symbol stops just above the reels we bounce them in for a brief moment
            if(this._stopSymbols[0].symbolName == "C" && this._stopSymbols[1].symbolName != "C") {
                this._stopTimeline.add(this.bounce(0.5, 1.0));
            }
        } else {
            for (let i:number = 0; i < numberOfNormalSteps; i++) {
                const tween = this.step(reverse);
                this._stopTimeline.add(tween);
            }

            this._stopTimeline.add(this.bounce(0.1,0.5));
        }

        this._stopTriggerTl.add(gsap.to(this, {duration:this._stopTimeline.totalDuration()}));

        return this._stopTriggerTl.play(0);
    }


    public start(reverse:boolean = false):gsap.core.Timeline {
        this._startedReverse = reverse;
        const tl:gsap.core.Timeline = gsap.timeline({
            onStart:() => {
                this.state = SimpleReelModelState.START;
                this._stopSymbols = [];
            },
            onComplete:() => {
                this.startSpin(reverse);
            }
        });

        const tween = this.step(reverse);
        tween.vars.ease = "back.in(3)";
        tween.totalDuration(this.stepDuration * 8);

        tl.add(tween);

        return tl;
    }

    public windUp(windFactor:number = 0.2, durationFactor:number = 1, reverse:boolean = false):gsap.core.Timeline {
        const tl = gsap.timeline();
        const wind = gsap.to(this.animatedStepData, {
            duration:this.stepDuration * durationFactor,
            //x:reverse ? this.stepSize.x * windFactor : -this.stepSize.x * windFactor,
            y:reverse ? this.stepSize * windFactor : -this.stepSize * windFactor,
            ease:"power2.out",
            onStart:() => {
                this.state = SimpleReelModelState.WIND_UP;
            },
            onUpdate:() => this.updatePosition(),
        });

        const back = gsap.to(this.animatedStepData, {
            duration:this.stepDuration * durationFactor,
            x:0,
            y:0,
            ease:"power2.in",
            onUpdate:() => this.updatePosition(),
        });

        tl.add(wind);
        tl.add(back);

        return tl;
    }

    public bounce(bounceFactor:number = 0.2, durationFactor:number = 1, reverse:boolean = false):gsap.core.Timeline {
        const tl = gsap.timeline();
        const overshoot = gsap.to(this.animatedStepData, {
            duration:this.stepDuration * durationFactor,
            //x:reverse ? -this.stepSize.x * bounceFactor : this.stepSize.x * bounceFactor,
            y:reverse ? -this.stepSize * bounceFactor : this.stepSize * bounceFactor,
            ease:"power2.out",
            onStart:() => {
                this.state = SimpleReelModelState.BOUNCE;
            },
            onUpdate:() => this.updatePosition(),
        });

        const back = gsap.to(this.animatedStepData, {
            duration:this.stepDuration * durationFactor * 2,
            x:0,
            y:0,
            ease:"power2.out",
            onUpdate:() => this.updatePosition(),
        });

        tl.add(overshoot);
        tl.add(back);

        return tl;
    }

    public nudge(reverse:boolean = false, isFinalNudge = false):gsap.core.Tween {
        const tween = this.step(reverse);
        tween.eventCallback("onStart", () => {
            this.state = SimpleReelModelState.NUDGE;
            this.onAnimationStart(tween, reverse, !isFinalNudge);
        });

        return tween;
    }

    private startSpin(reverse:boolean = false) {
        this._spinning = true;
        this.state = SimpleReelModelState.SPIN;
        this.masterSpinTimeline = gsap.timeline();
        // Cross-fade into blurred texture
        this.masterSpinTimeline.add(() => {
            this._symbols.forEach(sym => {
                if(sym.symbolName != "C") {
                    if (sym instanceof BrickSnake2000Symbol)
                        sym.crossFadeIntoSpin(0.3);
                }
            })
        })
        this.masterSpinTimeline.add(this.spin(reverse), "<");
    }

    private spin(reverse:boolean = false):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline({
            onComplete:() => {
                if (this._spinning) {
                    this.masterSpinTimeline?.add(this.spin(reverse));
                    //this.spin(reverse);
                }
            }
        })
        tl.add(this.step(reverse));
        return tl;
    }

    private step(reverse:boolean = false):gsap.core.Tween {
        const tween:gsap.core.Tween = gsap.to(this.animatedStepData, {
            duration:this.stepDuration,
            //x:reverse ? -this.stepSize.x : this.stepSize.x,
            y:reverse ? -this.stepSize : this.stepSize,
            ease:"none",
            onStart:() => this.onAnimationStart(tween, reverse),
            onUpdate:() => this.updatePosition(),
            onComplete:() => this.onAnimationComplete(reverse)
        });
        return tween;
    }


    private stepsAnticipation(steps:number, stopDuration:number, reverse:boolean = false):gsap.core.Tween {
        let currentMovedSteps:number = 0;
        return gsap.to(this.animatedStepData, {
            duration:stopDuration,
            y:reverse?-this.stepSize * steps : this.stepSize * steps,
            ease: CustomEase.create("custom", "M0,0 C0,0.358 0.241,0.467 0.5,0.5 0.767,0.567 1,0.696 1,1"),
            onStart: () => {
                for(let i:number = 0; i < steps; i++) {
                    this.addSymbol(reverse);
                    this.updateIndex(reverse);
                }
                this.updatePosition();
            },
            onUpdate: () => {
                const movedSteps = Math.floor((this.animatedStepData.y / this.stepSize) % this.stepSize);
                if(movedSteps != currentMovedSteps) {
                    this.updatePosition();
                    //Remove symbols that has moved outside the bottom of the reel
                    this._symbols.filter(sym => sym.position.y > 6 * this.stepSize).forEach(sym => {
                        const removedSym = this._symbols.splice(this._symbols.indexOf(sym), 1)[0];
                        this._delegate.removeSymbol(removedSym);
                    })

                    currentMovedSteps = movedSteps;
                } else {
                    this.updatePosition();
                }
            },
            onComplete: () => {
                this._symbols.filter(s => !this._stopSymbols.some(sym => sym == s)).forEach(sym => {
                    this._delegate.removeSymbol(sym);
                })
                this._symbols = [...this._stopSymbols];
                for(let i:number = -1; i < 5; i++) {
                    //this._symbols.push(this._stopSymbols[i+1]);
                    this._symbols[i+1].reelPosition = i;
                }

                this.animatedStepData.x = 0;
                this.animatedStepData.y = 0;

                this.updatePosition();
                //this.masterSpinTimeline?.kill();
                //const nonStoppedSymbols = this._symbols.filter(s => !this._stopSymbols.some(sym => sym == s));
                //this._symbols = this._symbols.filter(s => !nonStoppedSymbols.find(sym => sym == s));
            }
        });
    }

    private onAnimationStart(tween:gsap.core.Tween, reverse:boolean = false, xNudgedIn:boolean = false) {
        this._currentSpinStep = tween;
        this.addSymbol(reverse, xNudgedIn);
    }

    private onAnimationComplete(reverse:boolean = false) {
        for (let i:number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            symbol.reelPosition += reverse ? -1 : 1;
        }

        this.animatedStepData.x = 0;
        this.animatedStepData.y = 0;

        if (this.triggerStop) {
            this.triggerStop = false;
            this.masterSpinTimeline?.kill();
            this._stopTimeline?.play(0);
        }

        this.updateIndex(reverse);
        this.removeSymbol();
        this.updatePosition();
    }

    private updateIndex(reverse:boolean) {
        const valueChange = reverse ? 1 : -1
        if (this._stopping) {
            this.setStopIndex(this._stopIndex + valueChange);
        } else {
            this.setReelIndex(this._reelSetIndex + valueChange);
        }
    }

    private setStopIndex(value:number) {
        if (value >= this._stopSymbols.length) {
            value = 0;
        } else if (value < 0) {
            value = this._stopSymbols.length - 1;
        }
        this._stopIndex = value;
    }

    private setReelIndex(value:number) {
        if (value >= this.reelSet.length) {
            value = 0;
        } else if (value < 0) {
            value = this.reelSet.length - 1;
        }
        this._reelSetIndex = value;
    }

    private addSymbol(reverse:boolean, xNudgedIn:boolean = false) {
        let newSymbols = [];
        let newSymbolPosition = this._symbols[0].reelPosition - 1;

        if (reverse) {
            newSymbolPosition = this._symbols[this._symbols.length - 1].reelPosition + 1;
        }

        // a right proper hackjob but seems to get the job done at least
        const nextSymbol = this.getNextSymbol(newSymbolPosition, this.state, xNudgedIn);
        newSymbols.push(nextSymbol);

        this._symbols = this._symbols.concat(newSymbols);
        this._symbols.sort((a:any, b:any) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        });

    }

    private removeSymbol() {
        for (let i:number = this._symbols.length - 1; i >= 0; i--) {
            const symbol = this._symbols[i];
            if (symbol.reelPosition >= this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter) {
                //Remove from bottom
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this._delegate.removeSymbol(removedSymbol);
            }
            if (symbol.reelPosition < -this.paddingConfig.paddingBefore - this.paddingConfig.visualPaddingBefore) {
                //Remove from top
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this._delegate.removeSymbol(removedSymbol);
            }
        }
    }

    private updatePosition() {
        for (let i:number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            const basePosY = this.stepSize * symbol.reelPosition;
            symbol.position.y = basePosY + this.animatedStepData.y;
        }
    }

    public forcePositionUpdate() {
        this.updatePosition();
    }

    //Public utils
    public updatePaddingConfig(config:SimpleReelPaddingData) {

        const totalAfter = config.visualPaddingBefore + config.paddingBefore + config.visibleSymbols + config.visualPaddingAfter + config.paddingAfter;

        const oldBeforePadding = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        const newBeforePadding = config.visualPaddingBefore + config.paddingBefore;
        let beforeDiff = oldBeforePadding - newBeforePadding;


        if (beforeDiff > 0) {
            //If more padding than wanted, remove them
            const removedSymbols = this.symbols.splice(0, beforeDiff);
            for (let sym of removedSymbols) {
                this._delegate.removeSymbol(sym);
            }
        } else {
            //If less, add new padding symbols
            beforeDiff = Math.abs(beforeDiff);
            const newSymbols = [];
            for (let i:number = 0; i < beforeDiff; i++) {
                newSymbols.push(this.createNewSymbol(this.getRandomSymbolVisualPaddingSymbol(), 0, SimpleReelModelState.IDLE));
            }
            this.symbols.unshift(...newSymbols);
        }

        let totalDiff = this.symbols.length - totalAfter;
        if (totalDiff > 0) {
            const removedSymbols = this.symbols.splice(-totalDiff, totalDiff);
            for (let sym of removedSymbols) {
                this._delegate.removeSymbol(sym);
            }
        } else {
            //If less, add new padding symbols
            totalDiff = Math.abs(totalDiff);
            const newSymbols = [];
            for (let i:number = 0; i < totalDiff; i++) {
                newSymbols.push(this.createNewSymbol(this.getRandomSymbolVisualPaddingSymbol(), 0, SimpleReelModelState.IDLE));
            }
            this.symbols.push(...newSymbols);
        }

        this.paddingConfig.visualPaddingBefore = config.visualPaddingBefore;
        this.paddingConfig.paddingBefore = config.paddingBefore;
        this.paddingConfig.visibleSymbols = config.visibleSymbols;
        this.paddingConfig.paddingAfter = config.paddingAfter;
        this.paddingConfig.visualPaddingAfter = config.visualPaddingAfter;

        for (let i:number = 0; i < this.symbols.length; i++) {
            this.symbols[i].reelPosition = i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore;
        }
        this.updatePosition();
    }

    public resize(stepSize:number, duration:number, ease:gsap.EaseFunction):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline({onUpdate:() => this.updatePosition()});
        tl.add(gsap.to(this, {duration:duration, ease:ease, stepSize:stepSize}))
        return tl;
    }

    public visibleIndexToRealIndex(visibleIndex:number) {
        const paddingBefore = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        return visibleIndex + paddingBefore;
    }

    public getAllVisibleSymbols():BrickSnake2000Symbol [] {
        const paddingBefore = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        if (this._stopSymbols.length > 0) {
            return this._stopSymbols.slice(paddingBefore, paddingBefore + this.paddingConfig.visibleSymbols);
        } else {
            return this._symbols.slice(paddingBefore, paddingBefore + this.paddingConfig.visibleSymbols);
        }
    }

    public getAllSymbols():BrickSnake2000Symbol[] {
        return this._symbols;
    }

    public getVisibleSymbolAt(symbolIndex:number):BrickSnake2000Symbol {
        const index = this.visibleIndexToRealIndex(symbolIndex);

        if (this._stopSymbols.length > 0) {
            return this._stopSymbols[index];
        } else {
            return this._symbols[index];
        }
    }

    public getDataForPositionable(positionable:BrickSnake2000Symbol):BrickSnake2000Symbol | undefined {
        const symbols = this.getAllVisibleSymbols();
        for (let symbolData of symbols) {
            if (symbolData == positionable) {
                return symbolData;
            }
        }

        return;
    }

    public replaceSymbolAt(reelPosition:number, newSym:BrickSnake2000Symbol):void {
        //const index = this._stopSymbols.indexOf(this._stopSymbols.find((sym) => sym.reelPosition == reelPosition)!);
        const index = reelPosition + 1;
        if (this._stopSymbols.length > 0)
            this.stopSymbols[index] = newSym;
        this.symbols[index] = newSym;
        //this._delegate.removeSymbol(oldSym);
    }

}